import {
  IconButton,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material'
import React from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import ReactJson from 'react-json-view'
import { useSelector } from 'react-redux'
import { selectThemeMode } from '../Redux/Theme'
import { Delete } from '@mui/icons-material'
import getStorage from '../../Common/Services/Storage'
import { useForceUpdate } from '../../Common/Utils/Hooks'
import history from '../../Core/history'
import Config from '../Config'
import FullTextSearchField from '../../Common/Components/FullTextSearchField'
import { setStateFromEvent } from '../../Common/Utils/Events'
import { always, either, includes, is, isEmpty, pipe, prop } from 'ramda'
const storageService = getStorage()

const Container = styled.div`
  width: 100%;
`
const Header = styled.div`
  align-items: center;
  background: ${({ theme }) => theme.palette.contrastMedium.main};
  display: flex;
  justify-content: space-between;
  padding: 0.5rem;
`

const Info = styled.p`
  color: ${({ theme }) => theme.palette.contrastHigh.main};
  font-size: 14px;
`

const SectionHeader = styled.div`
  background: ${({ theme }) => theme.palette.contrastLight.main};
  margin-top: 1rem;
  padding: 0.5rem;
`

const LocalStorageManager = () => {
  const { t } = useTranslation()
  const theme = useSelector(selectThemeMode)
  const forceUpdate = useForceUpdate()
  const [filterValue, setFilterValue] = React.useState('')

  const sections = {
    settings: [],
    changelistTableSettings: [],
    changelistTableFilters: [],
    changelistTableResizing: [],
    dapReportLayout: [],
    localReportLayout: [],
    unused: [],
  }

  Object.keys(localStorage).forEach((key) => {
    let section = null
    if (/PROMETHEUS_config[A-Z]/.test(key)) {
      section = 'settings'
    } else if (/CHANGELIST_TABLE_SETTINGS/.test(key)) {
      section = 'changelistTableSettings'
    } else if (/CHANGELIST_TABLE_FILTERS/.test(key)) {
      section = 'changelistTableFilters'
    } else if (/CHANGELIST_TABLE_RESIZING/.test(key)) {
      section = 'changelistTableResizing'
    } else if (/dap-report-layout/.test(key)) {
      section = 'dapReportLayout'
    } else if (/local-report-layout/.test(key)) {
      section = 'localReportLayout'
    } else if (/home-dashboard-device-selected/.test(key)) {
      section = 'unused'
    } else if (/home-dashboard-vehicle-selected/.test(key)) {
      section = 'unused'
    }

    if (section) {
      sections[section].push({
        key,
        value: localStorage[key],
      })
    }
  })

  const handleRemoveItem = (key) => () => {
    storageService.remove(key.replace('PROMETHEUS_', ''))
    forceUpdate()
  }

  return (
    <Container>
      <Header>
        <span>{t('common:ui.LocalStorageUsage')}</span>
        <span>{storageService.sizeKb()}/5.00 MB</span>
      </Header>
      <Info>{t('common:ui.LocalStorageUsageInfo')}</Info>
      <FullTextSearchField
        style={{ marginLeft: 'auto' }}
        value={filterValue}
        onChange={setStateFromEvent(setFilterValue)}
        fields={[t('common:fields.key')]}
      />

      {!!sections.unused.length && (
        <>
          <SectionHeader>{t('common:ui.UnusedShouldBeRemoved')}</SectionHeader>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('common:ui.Item')}</TableCell>
                <TableCell style={{ width: '410px' }}>{t('common:ui.Data')}</TableCell>
                <TableCell style={{ width: '60px' }}>
                  {t('common:ui.Size')}
                </TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sections.unused.filter(either(always(isEmpty(filterValue)), pipe(prop('key'), includes(filterValue.toLowerCase())))).map(({ key, value }) => {
                const item = key.replace(
                  'PROMETHEUS_',
                  '',
                ).replace(/-/g, ' ')
                return (
                  <TableRow key={key}>
                    <TableCell>{item}</TableCell>
                    <TableCell>
                      <ReactJson
                        style={{
                          width: '100%',
                          maxHeight: '200px',
                          overflow: 'auto',
                        }}
                        src={JSON.parse(value)}
                        collapsed={0}
                        theme={theme === 'light' ? 'rjv-default' : 'monokai'}
                        name="ITEM"
                        enableClipboard={false}
                      />
                    </TableCell>
                    <TableCell style={{ whiteSpace: 'nowrap' }}>
                      {storageService.sizeKb(key.replace('PROMETHEUS_', ''))} kB
                    </TableCell>
                    <TableCell style={{ width: '20px' }}>
                      <IconButton size="small" onClick={handleRemoveItem(key)}>
                        <Delete style={{ fontSize: '16px' }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </>
      )}

      {!!sections.changelistTableSettings.length && (
        <>
          <SectionHeader>{t('common:ui.TablesSettings')}</SectionHeader>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('common:ui.Table')}</TableCell>
                <TableCell style={{ width: '410px' }}>{t('common:ui.Data')}</TableCell>
                <TableCell style={{ width: '60px' }}>
                  {t('common:ui.Size')}
                </TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sections.changelistTableSettings.filter(either(always(isEmpty(filterValue)), pipe(prop('key'), includes(filterValue.toLowerCase())))).map(({ key, value }) => {
                return (
                  <TableRow key={key}>
                    <TableCell>
                      {key
                        .replace('PROMETHEUS_', '')
                        .replace('_CHANGELIST_TABLE_SETTINGS', '')
                        .replace(/-/g, ' ')}
                    </TableCell>
                    <TableCell>
                      <ReactJson
                        style={{
                          width: '100%',
                          maxHeight: '100px',
                          overflow: 'auto',
                        }}
                        src={JSON.parse(value)}
                        collapsed={0}
                        theme={theme === 'light' ? 'rjv-default' : 'monokai'}
                        name="SETTINGS"
                        enableClipboard={false}
                      />
                    </TableCell>
                    <TableCell style={{ whiteSpace: 'nowrap' }}>
                      {storageService.sizeKb(key.replace('PROMETHEUS_', ''))} kB
                    </TableCell>
                    <TableCell style={{ width: '20px' }}>
                      <IconButton size="small" onClick={handleRemoveItem(key)}>
                        <Delete style={{ fontSize: '16px' }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </>
      )}

      {!!sections.changelistTableFilters.length && (
        <>
          <SectionHeader>{t('common:ui.TablesFilters')}</SectionHeader>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('common:ui.Table')}</TableCell>
                <TableCell style={{ width: '410px' }}>{t('common:ui.Data')}</TableCell>
                <TableCell style={{ width: '60px' }}>
                  {t('common:ui.Size')}
                </TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sections.changelistTableFilters.filter(either(always(isEmpty(filterValue)), pipe(prop('key'), includes(filterValue.toLowerCase())))).map(({ key, value }) => {
                return (
                  <TableRow key={key}>
                    <TableCell>
                      {key
                        .replace('PROMETHEUS_', '')
                        .replace('_CHANGELIST_TABLE_FILTERS', '')
                        .replace(/-/g, ' ')}
                    </TableCell>
                    <TableCell>
                      <ReactJson
                        style={{
                          width: '100%',
                          maxHeight: '100px',
                          overflow: 'auto',
                        }}
                        src={JSON.parse(value)}
                        collapsed={0}
                        theme={theme === 'light' ? 'rjv-default' : 'monokai'}
                        name="FILTERS"
                        enableClipboard={false}
                      />
                    </TableCell>
                    <TableCell style={{ whiteSpace: 'nowrap' }}>
                      {storageService.sizeKb(key.replace('PROMETHEUS_', ''))} kB
                    </TableCell>
                    <TableCell style={{ width: '20px' }}>
                      <IconButton size="small" onClick={handleRemoveItem(key)}>
                        <Delete style={{ fontSize: '16px' }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </>
      )}

      {!!sections.changelistTableResizing.length && (
        <>
          <SectionHeader>{t('common:ui.TablesColumnsWidths')}</SectionHeader>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('common:ui.Table')}</TableCell>
                <TableCell style={{ width: '410px' }}>{t('common:ui.Data')}</TableCell>
                <TableCell style={{ width: '60px' }}>
                  {t('common:ui.Size')}
                </TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sections.changelistTableResizing.filter(either(always(isEmpty(filterValue)), pipe(prop('key'), includes(filterValue.toLowerCase())))).map(({ key, value }) => {
                return (
                  <TableRow key={key}>
                    <TableCell>
                      {key
                        .replace('PROMETHEUS_', '')
                        .replace('_CHANGELIST_TABLE_RESIZING', '')
                        .replace(/-/g, ' ')}
                    </TableCell>
                    <TableCell>
                      <ReactJson
                        style={{
                          width: '100%',
                          maxHeight: '100px',
                          overflow: 'auto',
                        }}
                        src={JSON.parse(value)}
                        collapsed={0}
                        theme={theme === 'light' ? 'rjv-default' : 'monokai'}
                        name="FILTERS"
                        enableClipboard={false}
                      />
                    </TableCell>
                    <TableCell style={{ whiteSpace: 'nowrap' }}>
                      {storageService.sizeKb(key.replace('PROMETHEUS_', ''))} kB
                    </TableCell>
                    <TableCell style={{ width: '20px' }}>
                      <IconButton size="small" onClick={handleRemoveItem(key)}>
                        <Delete style={{ fontSize: '16px' }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </>
      )}

      {!!sections.dapReportLayout.length && (
        <>
          <SectionHeader>{t('common:ui.DapReportsLayout')}</SectionHeader>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('common:ui.Report')}</TableCell>
                <TableCell style={{ width: '410px' }}>{t('common:ui.Data')}</TableCell>
                <TableCell style={{ width: '60px' }}>
                  {t('common:ui.Size')}
                </TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sections.dapReportLayout.filter(either(always(isEmpty(filterValue)), pipe(prop('key'), includes(filterValue.toLowerCase())))).map(({ key, value }) => {
                const reportId = key.replace(
                  'PROMETHEUS_dap-report-layout-',
                  '',
                )
                return (
                  <TableRow key={key}>
                    <TableCell>
                      <Link
                        style={{ cursor: 'pointer ' }}
                        onClick={() =>
                          history.push(
                            Config.urls.reports.detail.replace(
                              ':reportId',
                              reportId,
                            ),
                          )
                        }
                      >
                        {reportId}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <ReactJson
                        style={{
                          width: '100%',
                          maxHeight: '200px',
                          overflow: 'auto',
                        }}
                        src={JSON.parse(value)}
                        collapsed={0}
                        theme={theme === 'light' ? 'rjv-default' : 'monokai'}
                        name="LAYOUT"
                        enableClipboard={false}
                      />
                    </TableCell>
                    <TableCell style={{ whiteSpace: 'nowrap' }}>
                      {storageService.sizeKb(key.replace('PROMETHEUS_', ''))} kB
                    </TableCell>
                    <TableCell style={{ width: '20px' }}>
                      <IconButton size="small" onClick={handleRemoveItem(key)}>
                        <Delete style={{ fontSize: '16px' }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </>
      )}

      {!!sections.localReportLayout.length && (
        <>
          <SectionHeader>{t('common:ui.LocalReportsLayout')}</SectionHeader>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('common:ui.Report')}</TableCell>
                <TableCell style={{ width: '410px' }}>{t('common:ui.Data')}</TableCell>
                <TableCell style={{ width: '60px' }}>
                  {t('common:ui.Size')}
                </TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sections.localReportLayout.filter(either(always(isEmpty(filterValue)), pipe(prop('key'), includes(filterValue.toLowerCase())))).map(({ key, value }) => {
                const reportId = key.replace(
                  'PROMETHEUS_local-report-layout-',
                  '',
                )
                return (
                  <TableRow key={key}>
                    <TableCell>
                      <Link
                        style={{ cursor: 'pointer ' }}
                        onClick={() =>
                          history.push(
                            Config.urls.reports.localDetail.replace(
                              ':reportId',
                              reportId,
                            ),
                          )
                        }
                      >
                        {reportId}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <ReactJson
                        style={{
                          width: '100%',
                          maxHeight: '200px',
                          overflow: 'auto',
                        }}
                        src={JSON.parse(value)}
                        collapsed={0}
                        theme={theme === 'light' ? 'rjv-default' : 'monokai'}
                        name="LAYOUT"
                        enableClipboard={false}
                      />
                    </TableCell>
                    <TableCell style={{ whiteSpace: 'nowrap' }}>
                      {storageService.sizeKb(key.replace('PROMETHEUS_', ''))} kB
                    </TableCell>
                    <TableCell style={{ width: '20px' }}>
                      <IconButton size="small" onClick={handleRemoveItem(key)}>
                        <Delete style={{ fontSize: '16px' }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </>
      )}

      {!!sections.settings.length && (
        <>
          <SectionHeader>{t('common:ui.Settings')}</SectionHeader>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('common:ui.Setting')}</TableCell>
                <TableCell style={{ width: '410px' }}>{t('common:ui.Data')}</TableCell>
                <TableCell style={{ width: '60px' }}>
                  {t('common:ui.Size')}
                </TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sections.settings.filter(either(always(isEmpty(filterValue)), pipe(prop('key'), includes(filterValue.toLowerCase())))).map(({ key, value }) => {
                return (
                  <TableRow key={key}>
                    <TableCell>
                      {key
                        .replace('PROMETHEUS_config', '')
                        .replace(/([A-Z])/g, ' $1')
                        .replace(/-/g, ' ')}
                    </TableCell>
                    <TableCell>
                      {is(Object, JSON.parse(value)) ? (
                        <ReactJson
                          style={{
                            width: '100%',
                            maxHeight: '100px',
                            overflow: 'auto',
                          }}
                          src={JSON.parse(value)}
                          collapsed={0}
                          theme={theme === 'light' ? 'rjv-default' : 'monokai'}
                          name="SETTING"
                          enableClipboard={false}
                        />
                      ) : value}
                    </TableCell>
                    <TableCell style={{ whiteSpace: 'nowrap' }}>
                      {storageService.sizeKb(key.replace('PROMETHEUS_', ''))} kB
                    </TableCell>
                    <TableCell style={{ width: '20px' }}>
                      <IconButton size="small" onClick={handleRemoveItem(key)}>
                        <Delete style={{ fontSize: '16px' }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </>
      )}
    </Container>
  )
}

export default LocalStorageManager
